import { Injectable, NgZone } from '@angular/core';
import { SharedDataService } from './shared.data.service';
import { UserService } from '../../services/core/user.service';
import { UserStatusService } from './header/statistics-dropdown/user-status.service';

/**
 * User Activity Tracking Service
 *
 * Tracks user activity (mouse movement, clicks, keyboard input) and
 * evaluates activity on a fixed interval.
 *
 * Activity Definition:
 * - Mouse movement (mousemove)
 * - Mouse click (click)
 * - Keyboard input (keydown)
 *
 * Timing:
 * - Activity ping: fixed interval (3m)
 * - Status sync: fixed interval (2m)
 * - Logs result to console (for now)
 *
 * Future Enhancement:
 * - Will call backend API to update last activity timestamp
 */
@Injectable({
  providedIn: 'root',
})
export class UserActivityService {
  // Activity POST interval (ms)
  private readonly activityIntervalMs = 180000; // 3 minutes

  // Status GET interval (ms)
  private readonly statusIntervalMs = 120000; // 2 minutes

  // Track if activity happened in the current period
  private activityDetected: boolean = false;

  // Timer reference for interval timers
  private activityTimer: any = null;
  private statusTimer: any = null;

  // Track if service is currently tracking
  private isTracking: boolean = false;

  // Track if status was set by user or backend cron
  private isSelfUpdate: boolean = false;

  // Track current user status
  private currentUserStatus: string = '';

  // Bound event handlers (for proper cleanup)
  private boundMouseMoveHandler = this.onUserActivity.bind(this);
  private boundClickHandler = this.onUserActivity.bind(this);
  private boundKeydownHandler = this.onUserActivity.bind(this);

  constructor(
    private ngZone: NgZone,
    private sharedDataService: SharedDataService,
    private currentUser: UserService,
    private userStatusService: UserStatusService
  ) {}

  /**
   * Start tracking user activity
   * Registers event listeners and starts interval timer
   */
  startTracking(): void {
    if (this.isTracking) {
      console.warn('[UserActivity] Already tracking');
      return;
    }

    console.log('[UserActivity] Starting activity tracking...');
    this.isTracking = true;
    this.activityDetected = false;

    // Initialize current status from localStorage
    this.currentUserStatus = this.currentUser.getUserStatus() || '';

    // Register event listeners outside Angular zone for performance
    this.ngZone.runOutsideAngular(() => {
      document.addEventListener('mousemove', this.boundMouseMoveHandler, {
        passive: true,
      });
      document.addEventListener('click', this.boundClickHandler, {
        passive: true,
      });
      document.addEventListener('keydown', this.boundKeydownHandler, {
        passive: true,
      });
    });

    // Start interval timers
    this.startTimer();
    this.startStatusTimer();
  }

  /**
   * Stop tracking user activity
   * Removes event listeners and clears timer
   */
  stopTracking(): void {
    if (!this.isTracking) {
      return;
    }

    console.log('[UserActivity] Stopping activity tracking...');
    this.isTracking = false;

    // Remove event listeners
    document.removeEventListener('mousemove', this.boundMouseMoveHandler);
    document.removeEventListener('click', this.boundClickHandler);
    document.removeEventListener('keydown', this.boundKeydownHandler);

    // Clear timer
    if (this.activityTimer) {
      clearInterval(this.activityTimer);
      this.activityTimer = null;
    }

    if (this.statusTimer) {
      clearInterval(this.statusTimer);
      this.statusTimer = null;
    }
  }

  /**
   * Event handler for user activity
   * Called when mousemove, click, or keydown events occur
   */
  private onUserActivity(): void {
    if (!this.activityDetected) {
      this.activityDetected = true;

      // If user was "away" (auto-set by backend) and becomes active, set to "available"
      this.handleActivityAfterAway();
    }
  }

  /**
   * Handle activity detection after user was away
   * If status is "away" and was set by backend (is_self_update = 0),
   * immediately call ping-activity and set status to "available"
   */
  private handleActivityAfterAway(): void {
    // Get fresh status from localStorage to ensure accuracy
    const currentStatus = this.currentUser.getUserStatus() || '';
    const isAway = currentStatus === 'away';
    const wasAutoSetByBackend = !this.isSelfUpdate;

    if (isAway && wasAutoSetByBackend) {
      // Step 1: Immediately call ping-activity API to notify backend
      this.sharedDataService.pingUserActivity({}).subscribe({
        next: () => {
          // Step 2: After successful ping, set status to available (optimistic update)
          const availableStatus = 'available';
          this.currentUser.setUserStatus(availableStatus);
          this.userStatusService.setCurrentStatus(availableStatus);

          // Update local tracking
          this.currentUserStatus = availableStatus;
          this.isSelfUpdate = true; // Mark as self-update to prevent re-triggering
        },
        error: (error) => {
          console.error('[UserActivity] Failed to send immediate ping', error);
          // Even if ping fails, set status to available (optimistic)
          const availableStatus = 'available';
          this.currentUser.setUserStatus(availableStatus);
          this.userStatusService.setCurrentStatus(availableStatus);
          this.currentUserStatus = availableStatus;
          this.isSelfUpdate = true;
        }
      });
    }
  }

  /**
   * Start the interval timer
   */
  private startTimer(): void {
    // Clear any existing timer
    if (this.activityTimer) {
      clearInterval(this.activityTimer);
    }

    // Set interval for evaluating activity
    this.activityTimer = setInterval(() => {
      this.evaluateActivity();
    }, this.activityIntervalMs);
  }

  /**
   * Start the status sync interval timer
   */
  private startStatusTimer(): void {
    if (this.statusTimer) {
      clearInterval(this.statusTimer);
    }

    this.statusTimer = setInterval(() => {
      this.syncUserStatus();
    }, this.statusIntervalMs);
  }

  /**
   * Evaluate activity after each interval
   * Logs to console, optionally calls backend, and resets activity flag
   */
  private evaluateActivity(): void {
    if (this.activityDetected) {
      this.sharedDataService.pingUserActivity({}).subscribe({
        next: () => {
          console.log('[UserActivity] Ping activity POST sent');
        },
        error: (error) => {
          console.error('[UserActivity] Failed to send ping activity', error);
        },
      });
    }

    // Reset activity flag for next period
    this.activityDetected = false;
  }

  /**
   * Sync user status from backend every interval
   */
  private syncUserStatus(): void {
    this.sharedDataService.getUserStatus().subscribe({
      next: (response: any) => {
        const statusData = response?.data?.data;
        const statusSlug = statusData?.user_status;
        const isSelfUpdate = statusData?.is_self_update === 1;

        if (!statusSlug) {
          return;
        }

        // Track is_self_update flag and current status
        this.isSelfUpdate = isSelfUpdate;
        this.currentUserStatus = statusSlug;

        // Update local user state and broadcast for UI (badge/text)
        this.currentUser.setUserStatus(statusSlug);
        this.userStatusService.setCurrentStatus(statusSlug);
      },
      error: (error) => {
        console.error('[UserActivity] Failed to sync user status', error);
      },
    });
  }

  /**
   * TODO: Future method to call backend API
   *
   * private updateLastActivityOnBackend(): void {
   *   this.backendApiService.patchApi('user-activity', {
   *     last_activity: new Date().toISOString()
   *   }).subscribe({
   *     next: (response) => {
   *       console.log('Last activity updated on backend');
   *     },
   *     error: (error) => {
   *       console.error('Failed to update last activity:', error);
   *     }
   *   });
   * }
   */
}
